import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  X,
  CheckCircle2,
  Users,
  Sparkles,
  Gift,
  Tag,
  Truck,
  CreditCard,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  HeartHandshake,
  Lock,
  Plus,
  Minus,
  Check,
  AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { processRazorpayPayment, verifyRazorpayPayment } from '../../lib/razorpay';
import { toast } from 'sonner';
import API from '../../config/api';
import { trackMetaEvent, getMetaContext } from '../../lib/meta';
import OfferingItem from './OfferingItem';
import PrasadShipping, { usePrasadShipping } from './PrasadShipping';

const DAKSHINA_PRESETS = [51, 101, 201, 501, 1001];

const DEFAULT_GOTRAS = [
  'Kashyap',
  'Bharadwaj',
  'Gautam',
  'Vashistha',
  'Vishvamitra',
  'Jamadagni',
  'Atri',
  'Agastya',
  'Sandilya',
  'Kaushik',
  'Garga',
  'Parashar',
  'Harita',
  'Vatsa',
  'Upamanyu',
  'Mudgala',
  'Sankrithi'
];

export default function PujaCheckoutModal({
  isOpen,
  onClose,
  puja,
  selectedPackage,
  temple
}) {
  const { user } = useAuth();
  const checkoutTracked = useRef(false);
  const verificationKey = `pending_puja_verification_${puja?.id || ''}_${selectedPackage?.id || selectedPackage?.name || ''}`;

  // Wizard Step: 1: Contact, 2: Bhet, 3: Devotees/Prasad, 4: Wish, 5: Payment, 6: Success
  const [step, setStep] = useState(() => localStorage.getItem(verificationKey) ? 5 : 1);

  // Step 2: Bhet, Dakshina, Products, Coupon
  const [bhetSelections, setBhetSelections] = useState({});
  const [availableBhets, setAvailableBhets] = useState([]);
  const [dakshinaPreset, setDakshinaPreset] = useState(101);
  const [customDakshina, setCustomDakshina] = useState('');
  const [storeProducts, setStoreProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Step 3: Devotee & Prasad Details
  const [contactInfo, setContactInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    whatsapp: user?.phone?.replace(/\D/g, '').slice(-10) || ''
  });

  const packageCapacity = Number(selectedPackage?.devotees_count);
  const numDevotees = Number.isInteger(packageCapacity) && packageCapacity > 0 ? packageCapacity : 1;
  const [devotees, setDevotees] = useState(() => [
    { name: user?.name || '', gotra: 'Kashyap', isUnknownGotra: false }
  ]);
  const namedDevotees = devotees.filter(devotee => devotee.name.trim());
  const [gotraList, setGotraList] = useState(DEFAULT_GOTRAS);

  const [hasPrasad, setHasPrasad] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    name: user?.name || '',
    address: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    phone: user?.phone || ''
  });

  // Step 4: Puja Wish / Sankalp
  const [pujaWish, setPujaWish] = useState('');

  // Step 5: Payment
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' | 'cod' | 'upi'
  const [submitting, setSubmitting] = useState(false);
  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(() => {
    try { return JSON.parse(localStorage.getItem(verificationKey)) || null; }
    catch { return null; }
  });
  const [completedBooking, setCompletedBooking] = useState(null);

  // Fetch Gotras, Store Products, and Bhet items on mount
  useEffect(() => {
    if (!isOpen) return;

    // Prefill user contact details if user exists
    if (user) {
      setContactInfo({
        name: user.name || '',
        email: user.email || '',
        whatsapp: user.phone?.replace(/\D/g, '').slice(-10) || ''
      });
      setDeliveryAddress(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        phone: prev.phone || user.phone || ''
      }));
    }

    // Keep added devotees when revisiting checkout, without creating empty slots.
    setDevotees(prev => {
      const trimmed = prev.slice(0, numDevotees);
      // Default Devotee 1 to user's name if empty
      if (user?.name && !trimmed[0]?.name) {
        trimmed[0] = { ...trimmed[0], name: user.name };
      }
      return trimmed;
    });

    // Fetch dynamic Gotras
    axios.get(`${API}/gotras`)
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const names = res.data.map(g => g.name).filter(Boolean);
          setGotraList(Array.from(new Set(names)));
        }
      })
      .catch(() => {});

    // Fetch store products for upsell
    axios.get(`${API}/products?limit=6`)
      .then(res => {
        const prods = Array.isArray(res.data) ? res.data : (res.data.products || []);
        setStoreProducts(prods);
      })
      .catch(() => {});
  }, [isOpen, selectedPackage, user, numDevotees]);

  // Refresh visibility when opening checkout or returning from another tab.
  useEffect(() => {
    setAvailableBhets([]);
    setBhetSelections({});
    if (!isOpen) return;
    let cancelled = false;
    const refreshBhets = async () => {
      try {
        const { data } = await axios.get(`${API}/bhets`);
        if (!cancelled) setAvailableBhets(Array.isArray(data) ? data.filter(item => item.is_active === true) : []);
      } catch {
        if (!cancelled) setAvailableBhets([]);
      }
    };
    refreshBhets();
    window.addEventListener('focus', refreshBhets);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshBhets);
    };
  }, [isOpen, puja?.id]);

  // Calculations
  const packagePrice = selectedPackage?.price || puja?.price || 0;

  const bhetList = availableBhets.filter(item => (puja?.bhet_ids || []).includes(item.id));
  const bhetTotal = bhetList.reduce((sum, item) => sum + item.price * (bhetSelections[item.id] || 0), 0);

  const finalDakshina = customDakshina !== ''
    ? (parseInt(customDakshina) || 0)
    : (dakshinaPreset || 0);

  const productsTotal = Object.entries(selectedProducts).reduce((sum, [pId, qty]) => {
    const prod = storeProducts.find(p => p.id === pId);
    return sum + (prod ? (prod.sale_price || prod.price) * qty : 0);
  }, 0);

  const itemsSubtotal = packagePrice + bhetTotal + finalDakshina + productsTotal;
  const deliveryReady = Boolean(contactInfo.name.trim() && contactInfo.whatsapp.trim() && devotees[0]?.name.trim()
    && deliveryAddress.name.trim() && (deliveryAddress.phone || contactInfo.whatsapp).trim()
    && deliveryAddress.address.trim() && deliveryAddress.city.trim() && /^[1-9]\d{5}$/.test(deliveryAddress.pincode.trim()));
  const shipping = usePrasadShipping({
    enabled: isOpen && hasPrasad, ready: deliveryReady, pincode: deliveryAddress.pincode,
    subtotal: itemsSubtotal, cod: paymentMethod === 'cod',
  });
  const shippingCharge = hasPrasad && shipping.selected ? shipping.selected.rate : 0;
  const subtotalBeforeDiscount = itemsSubtotal + shippingCharge;
  const discountAmount = appliedCoupon ? appliedCoupon.discount : 0;
  const finalPayableTotal = Math.max(0, subtotalBeforeDiscount - discountAmount);

  useEffect(() => {
    if (!isOpen) {
      checkoutTracked.current = false;
      return;
    }
    if (checkoutTracked.current || !puja?.id) return;
    checkoutTracked.current = true;
    trackMetaEvent('InitiateCheckout', {
      currency: 'INR',
      value: packagePrice,
      content_type: 'product',
      content_ids: [String(puja.id)],
      num_items: 1
    });
  }, [isOpen, puja?.id, packagePrice]);

  // Apply Coupon Handler
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    setCouponLoading(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        cart_total: subtotalBeforeDiscount
      });
      if (res.data && res.data.valid) {
        setAppliedCoupon(res.data);
        toast.success(`Coupon ${res.data.code} applied! Saved ₹${res.data.discount}`);
      } else {
        toast.error(res.data?.detail || 'Invalid coupon code');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove Coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  // Validate the contact step before moving to offerings.
  const handleContactContinue = (e) => {
    e.preventDefault();
    if (!contactInfo.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(contactInfo.whatsapp.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit WhatsApp number');
      return;
    }
    setDevotees(prev => prev.map((devotee, index) => index === 0 && !devotee.name.trim()
      ? { ...devotee, name: contactInfo.name.trim() } : devotee));
    setDeliveryAddress(prev => ({ ...prev, name: prev.name || contactInfo.name.trim(), phone: prev.phone || contactInfo.whatsapp }));
    setStep(2);
  };

  // Validate Step 3 (Devotees)
  const validateStep3 = () => {
    if (!contactInfo.name?.trim()) {
      toast.error('Please enter your full contact name');
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(contactInfo.whatsapp.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit WhatsApp number');
      return false;
    }
    // At least first devotee name
    if (!devotees[0]?.name?.trim()) {
      toast.error('Please enter at least Devotee 1 name');
      return false;
    }
    // If Prasad selected, validate delivery address
    if (hasPrasad) {
      if (!deliveryReady) {
        toast.error('Please enter your full delivery address for sacred Prasad');
        return false;
      }
      if (!shipping.selected || shipping.loading) {
        toast.error('Please select a Prasad shipping option');
        return false;
      }
    }
    return true;
  };

  const completePaidBooking = (bookingId, payload, guestBookingToken) => {
    if (guestBookingToken) localStorage.setItem(`guest_booking_${bookingId}`, guestBookingToken);
    localStorage.removeItem(verificationKey);
    setPendingVerification(null);
    setCompletedBooking({ id: bookingId, ...payload, payment_status: 'paid' });
    setStep(6);
    toast.success('Sacred Puja booked and verified successfully!');
  };

  // Final Submit & Payment
  const handleFinalSubmit = async () => {
    if (pendingVerification) {
      setSubmitting(true);
      try {
        const { orderId, guestBookingToken, payload, response } = pendingVerification;
        const result = await verifyRazorpayPayment({ orderId, orderType: 'booking', guestBookingToken, response });
        completePaidBooking(result.booking_id || orderId, payload, guestBookingToken);
      } catch (error) {
        toast.error(error.response?.data?.detail || error.message || 'Payment verification failed. Please retry.');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!validateStep3()) {
      setStep(3);
      return;
    }
    setSubmitting(true);
    try {
      // Build selected Bhet array
      const bhetToSend = bhetList
        .filter(item => bhetSelections[item.id] > 0)
        .map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: bhetSelections[item.id],
          image_url: item.image_url
        }));

      // Build selected products array
      const prodsToSend = Object.entries(selectedProducts)
        .filter(([_, qty]) => qty > 0)
        .map(([pId, qty]) => {
          const p = storeProducts.find(item => item.id === pId);
          return {
            product_id: p.id,
            name: p.name,
            price: p.sale_price || p.price,
            quantity: qty,
            image_url: p.image_url
          };
        });

      // Devotees array
      const devoteeNames = [];
      const gotras = [];
      devotees.forEach(d => {
        if (d.name.trim()) {
          devoteeNames.push(d.name.trim());
          gotras.push(d.isUnknownGotra ? 'Kashyap' : (d.gotra || 'Kashyap'));
        }
      });

      const payload = {
        guest_booking: !user,
        contact: {
          name: contactInfo.name.trim(),
          email: contactInfo.email.trim(),
          whatsapp: contactInfo.whatsapp.replace(/\D/g, '')
        },
        puja_id: puja.id,
        temple_id: puja.temple_id || temple?.id || '',
        package_id: selectedPackage?.id || '',
        package_name: selectedPackage?.name || 'Vedic Package',
        package_price: packagePrice,
        puja_date: puja.puja_date || '',
        devotee_names: devoteeNames,
        gotras: gotras,
        devotees: devoteeNames.map((name, i) => ({ name, gotra: gotras[i] })),
        bhet_items: bhetToSend,
        dakshina: finalDakshina,
        store_products: prodsToSend,
        coupon_code: appliedCoupon ? appliedCoupon.code : '',
        discount: discountAmount,
        shipping_charge: shippingCharge,
        shipping_package: hasPrasad ? shipping.selected : null,
        has_prasad: hasPrasad,
        delivery_address: hasPrasad ? {
          ...deliveryAddress,
          phone: deliveryAddress.phone || contactInfo.whatsapp,
          whatsapp: contactInfo.whatsapp,
          email: contactInfo.email
        } : {},
        puja_wish: pujaWish,
        special_instructions: pujaWish,
        amount: finalPayableTotal,
        payment_method: paymentMethod,
        meta: getMetaContext()
      };

      const bookingRes = await axios.post(`${API}/bookings`, payload, {
        withCredentials: true
      });

      const bookingData = bookingRes.data;
      const bookingId = bookingData.booking_id;

      if (paymentMethod === 'razorpay' && finalPayableTotal > 0) {
        // The server stores a draft. A booking is created only after payment verification.
        setGatewayOpen(true);
        await processRazorpayPayment({
          orderId: bookingId,
          orderType: 'booking',
          guestBookingToken: bookingData.guest_booking_token,
          user: {
            ...user,
            name: contactInfo.name,
            phone: contactInfo.whatsapp,
            email: contactInfo.email
          },
          onSuccess: (result) => {
            setGatewayOpen(false);
            completePaidBooking(result.booking_id || bookingId, payload, bookingData.guest_booking_token);
          },
          onError: (errMsg) => {
            setGatewayOpen(false);
            toast.error(errMsg || 'Payment failed or cancelled.');
          },
          onDismiss: () => {
            setGatewayOpen(false);
            toast.info('Payment was cancelled. You can retry from checkout.');
          },
          onVerificationError: (errorMessage, response) => {
            setGatewayOpen(false);
            const verification = { orderId: bookingId, guestBookingToken: bookingData.guest_booking_token, payload, response };
            localStorage.setItem(verificationKey, JSON.stringify(verification));
            setPendingVerification(verification);
            toast.error(errorMessage || 'Payment received, but confirmation could not be completed. Retry verification.');
          }
        });
      } else {
        // COD or Zero amount
        if (bookingData.guest_booking_token) {
          localStorage.setItem(`guest_booking_${bookingId}`, bookingData.guest_booking_token);
        }
        setCompletedBooking({
          id: bookingId,
          ...payload,
          amount: finalPayableTotal,
          payment_status: 'pending'
        });
        setStep(6);
        toast.success('Puja booked successfully!');
      }
    } catch (err) {
      setGatewayOpen(false);
      console.error('Booking failed:', err);
      if (err.response?.status === 409 && hasPrasad) {
        shipping.retry();
        setStep(3);
      }
      toast.error(err.response?.data?.detail || 'Failed to complete booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div data-testid="puja-checkout" className="puja-checkout relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-[#E7D9BC] overflow-hidden my-6">

        {/* ═══ MODAL HEADER ═══ */}
        <div className="bg-gradient-to-r from-[#6E1B34] via-[#4E1225] to-[#2A1F16] text-white px-5 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-[#E8D5A8] font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-[#F2A65A]" />
              <span>Puja Checkout</span>
            </div>
            <h3 className="font-heading text-base sm:text-lg font-bold text-white truncate max-w-[280px] sm:max-w-md">
              {puja?.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ═══ STEP PROGRESS TABS (5 Steps) ═══ */}
        {step < 6 && (
          <div className="bg-[#FBF4E7] border-b border-[#E7D9BC] px-4 py-2.5 flex items-center justify-between text-[11px] font-bold overflow-x-auto scrollbar-none">
            {[
              { num: 1, label: 'Contact' },
              { num: 2, label: 'Bhet & Offerings' },
              { num: 3, label: 'Devotees & Prasad' },
              { num: 4, label: 'Puja Wish' },
              { num: 5, label: 'Payment' }
            ].map((s) => {
              const isActive = step === s.num;
              const isPast = step > s.num;
              return (
                <div
                  key={s.num}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full whitespace-nowrap ${
                    isActive
                      ? 'bg-[#6E1B34] text-white'
                      : isPast
                      ? 'text-[#2E7D45]'
                      : 'text-gray-400'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                    isActive ? 'bg-white text-[#6E1B34]' : isPast ? 'bg-[#2E7D45] text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {isPast ? '✓' : s.num}
                  </span>
                  <span>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ MODAL BODY ═══ */}
        <div className="p-5 sm:p-6 max-h-[75vh] overflow-y-auto space-y-6">

          {/* ═══════════════════ STEP 1: LOGIN ═══════════════════ */}
          {step === 1 && (
            <form onSubmit={handleContactContinue} className="space-y-5 max-w-md mx-auto">
              <div className="text-center">
                <h4 className="font-heading text-lg font-bold text-[#4E1225]">Your Booking Contact</h4>
                <p className="text-xs text-gray-500">Photos, video and booking updates will be sent to your WhatsApp number.</p>
              </div>
              <div className="pt-1 space-y-2">
                <Label htmlFor="booking-whatsapp" className="text-sm font-bold text-[#2A1F16]">WhatsApp Mobile Number *</Label>
                <div className="flex w-full overflow-hidden rounded-xl border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-[#d9711f]">
                  <span className="flex shrink-0 items-center whitespace-nowrap border-r border-gray-300 px-3 text-sm font-bold">+91</span>
                  <input id="booking-whatsapp" type="tel" inputMode="numeric" maxLength={10} required
                    value={contactInfo.whatsapp}
                    onChange={e => setContactInfo(prev => ({ ...prev, whatsapp: e.target.value.replace(/\D/g, '') }))}
                    placeholder="10-digit WhatsApp number" className="min-w-0 w-full h-11 px-3 text-sm outline-none rounded-r-xl focus:!outline-none focus:!ring-0 focus:!border-transparent active:!outline-none active:!ring-0 active:!border-transparent" />
                </div>
              </div>
              <div>
                <Label htmlFor="booking-name" className="text-sm font-bold text-[#2A1F16]">Name *</Label>
                <Input id="booking-name" required value={contactInfo.name}
                  onChange={e => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your full name" className="mt-2 h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="booking-email" className="text-xs text-gray-600">Email address (optional)</Label>
                <Input id="booking-email" type="email" value={contactInfo.email}
                  onChange={e => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="For your receipt" className="mt-1 rounded-xl" />
              </div>
              <Button type="submit" className="w-full btn-saffron py-2.5 rounded-xl font-bold text-sm">
                Continue to Offerings <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
          {/* ═══════════════════ STEP 2: BHET & OFFERINGS ═══════════════════ */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Selected Package Banner */}
              <div className="p-3 bg-[#FBF4E7] rounded-2xl border border-[#E7D9BC] flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-[#B85A15] uppercase tracking-wider block">
                    Selected Package
                  </span>
                  <p className="font-heading font-bold text-sm text-[#4E1225]">
                    {selectedPackage?.name || 'Standard Package'} &bull; {selectedPackage?.devotees_count || 1} {selectedPackage?.devotees_count === 1 ? 'Devotee' : 'Devotees'}
                  </p>
                </div>
                <span className="font-price font-black text-lg text-[#6E1B34]">
                  ₹{packagePrice.toLocaleString()}
                </span>
              </div>

              {/* Bhet at Temple */}
              {bhetList.length > 0 && <div>
                <h4 className="font-heading text-sm sm:text-base font-bold text-[#4E1225] flex items-center gap-1.5 mb-1">
                  <Gift className="w-4 h-4 text-[#D9711F]" />
                  <span>Offer Bhet at {temple?.name || 'Sacred Temple'} with your puja</span>
                </h4>
                <p className="text-xs text-gray-500 mb-3">
                  Sacred offerings presented directly at the sanctum sanctorum on your behalf
                </p>

                <div className="space-y-2.5">
                  {bhetList.map(item => (
                    <OfferingItem key={item.id} item={item} quantity={bhetSelections[item.id] || 0}
                      onQuantityChange={quantity => setBhetSelections(prev => ({ ...prev, [item.id]: quantity }))} />
                  ))}
                </div>
              </div>}

              {/* Pandit Dakshina */}
              <div className="pt-3 border-t border-gray-100">
                <h4 className="font-heading text-sm font-bold text-[#4E1225] flex items-center gap-1.5 mb-1">
                  <HeartHandshake className="w-4 h-4 text-[#D9711F]" />
                  <span>Add Dakshina for Puja 🙏🏻</span>
                </h4>
                <p className="text-xs text-gray-500 mb-3">
                  Offer a voluntary gratitude blessing for the Vedic Brahmins conducting your ritual
                </p>

                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {DAKSHINA_PRESETS.map((amt) => {
                    const isSelected = customDakshina === '' && dakshinaPreset === amt;
                    return (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => {
                          setDakshinaPreset(amt);
                          setCustomDakshina('');
                        }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                          isSelected
                            ? 'bg-[#6E1B34] text-white border-[#6E1B34] shadow-xs'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#D9711F]'
                        }`}
                      >
                        ₹{amt}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      setDakshinaPreset(0);
                      setCustomDakshina('');
                    }}
                    className={`px-2.5 py-1.5 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-700 ${
                      dakshinaPreset === 0 && customDakshina === '' ? 'underline font-bold' : ''
                    }`}
                  >
                    No Dakshina
                  </button>
                </div>

                {/* Custom Dakshina */}
                <div className="flex items-center gap-2 max-w-xs">
                  <span className="text-xs text-gray-500 font-medium">Add Your Own:</span>
                  <div className="relative flex-1">
                    <span className="absolute left-2.5 top-2 text-xs text-gray-400">₹</span>
                    <Input
                      type="number"
                      placeholder="e.g. 500"
                      value={customDakshina}
                      onChange={(e) => {
                        setCustomDakshina(e.target.value);
                        setDakshinaPreset(0);
                      }}
                      className="h-8 pl-6 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Offer Products from Store */}
              {storeProducts.length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <h4 className="font-heading text-sm font-bold text-[#4E1225] flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-4 h-4 text-[#D9711F]" />
                    <span>Offer Products</span>
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Consecrated divine offerings from our store to receive with your puja blessings
                  </p>

                  <div className="space-y-2.5">
                    {storeProducts.slice(0, 4).map(prod => (
                      <OfferingItem key={prod.id} item={prod} price={prod.sale_price || prod.price}
                        quantity={selectedProducts[prod.id] || 0}
                        onQuantityChange={quantity => setSelectedProducts(prev => ({ ...prev, [prod.id]: quantity }))} />
                    ))}
                  </div>
                </div>
              )}

              {/* Coupon Code Section */}
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Tag className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-2.5" />
                    <Input
                      placeholder="Enter sacred coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="pl-8 text-xs h-9 uppercase"
                    />
                  </div>
                  {appliedCoupon ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRemoveCoupon}
                      className="text-xs h-9 border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      disabled={couponLoading}
                      onClick={handleApplyCoupon}
                      className="btn-saffron text-xs h-9 px-4"
                    >
                      {couponLoading ? 'Checking...' : 'Apply'}
                    </Button>
                  )}
                </div>
                {appliedCoupon && (
                  <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                    ✓ Code {appliedCoupon.code} applied! Discount: ₹{appliedCoupon.discount}
                  </p>
                )}
              </div>

              {/* Step 2 Footer Navigation */}
              <div className="pt-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200">
                <div>
                  <span className="text-[10px] text-gray-500 uppercase block">Subtotal</span>
                  <span className="font-price font-bold text-base text-[#6E1B34]">
                    ₹{finalPayableTotal.toLocaleString()}
                  </span>
                </div>
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="text-xs rounded-xl">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Contact
                </Button>
                <Button
                  type="button"
                  onClick={() => setStep(3)}
                  className="btn-saffron py-2.5 px-6 rounded-xl font-bold text-xs shadow-md flex items-center gap-1.5"
                >
                  <span>Continue to Devotee Details</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════ STEP 3: PUJA & DEVOTEE DETAILS ═══════════════════ */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Devotee Details */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-heading text-sm font-bold text-[#4E1225] flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-[#D9711F]" />
                    <span>Enter Devotee Details ({numDevotees} {numDevotees === 1 ? 'Devotee' : 'Devotees'} Allowed)</span>
                  </h4>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Names &amp; Gotras will be chanted by Vedic priests during the ritual sankalp.
                  {numDevotees > 1 && ' One devotee is required; additional devotees are optional.'}
                </p>

                <div className="space-y-3">
                  {devotees.map((d, index) => (
                    <div key={index} data-testid="devotee-entry" className="p-3.5 bg-white rounded-2xl border border-gray-200 shadow-2xs space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-bold text-[#6E1B34]">Devotee {index + 1} {index === 0 && '(Primary Sankalp)'}</span>
                        {index === 0 && user?.name && (
                          <span className="text-[10px] text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 font-medium">
                            Defaulted to your account name (editable)
                          </span>
                        )}
                        {index > 0 && (
                          <button type="button" aria-label={`Remove Devotee ${index + 1}`}
                            onClick={() => setDevotees(prev => prev.filter((_, devoteeIndex) => devoteeIndex !== index))}
                            className="inline-flex items-center gap-1 py-1 text-xs font-semibold text-[#6E1B34] hover:underline">
                            <X className="h-3.5 w-3.5" /> Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <div className="min-w-0 flex flex-col gap-1">
                          <Label htmlFor={`devotee-name-${index}`} className="flex h-5 items-center text-[11px] text-gray-500">
                            Full Name {index === 0 ? '*' : '(optional)'}
                          </Label>
                          <Input
                            id={`devotee-name-${index}`}
                            required={index === 0}
                            placeholder={`e.g. Devotee ${index + 1}`}
                            value={d.name}
                            onChange={(e) => {
                              const name = e.target.value;
                              setDevotees(prev => prev.map((devotee, devoteeIndex) => devoteeIndex === index ? { ...devotee, name } : devotee));
                            }}
                            className="text-xs h-9"
                          />
                        </div>

                        <div className="min-w-0 flex flex-col gap-1">
                          <Label htmlFor={`devotee-gotra-${index}`} className="flex h-5 items-center text-[11px] text-gray-500">Gotra</Label>

                          {d.isUnknownGotra ? (
                            <div id={`devotee-gotra-${index}`} className="min-h-9 flex items-center px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-md text-xs text-[#B85A15] font-semibold">
                              Kashyap (Universal Vedic Gotra)
                            </div>
                          ) : (
                            <select
                              id={`devotee-gotra-${index}`}
                              value={d.gotra}
                              onChange={(e) => {
                                const gotra = e.target.value;
                                setDevotees(prev => prev.map((devotee, devoteeIndex) => devoteeIndex === index ? { ...devotee, gotra } : devotee));
                              }}
                              className="w-full h-9 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#6E1B34]"
                            >
                              {gotraList.map(g => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                          )}
                          <button type="button"
                            onClick={() => setDevotees(prev => prev.map((devotee, devoteeIndex) => devoteeIndex === index ? {
                              ...devotee,
                              isUnknownGotra: !devotee.isUnknownGotra,
                              gotra: devotee.isUnknownGotra ? devotee.gotra : 'Kashyap'
                            } : devotee))}
                            className="self-start py-1 text-left text-[10px] text-[#D9711F] font-semibold hover:underline">
                            {d.isUnknownGotra ? 'Select from list' : "I don't know my Gotra"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {devotees.length < numDevotees && (
                  <Button type="button" variant="outline" className="mt-3 gap-1.5 text-xs text-[#6E1B34]"
                    onClick={() => setDevotees(prev => prev.length < numDevotees
                      ? [...prev, { name: '', gotra: 'Kashyap', isUnknownGotra: false }]
                      : prev)}>
                    <Plus className="h-4 w-4" /> Add More Devotee
                  </Button>
                )}
              </div>

              {/* Prasad Preference */}
              <div className="pt-2 border-t border-gray-100">
                <div data-testid="prasad-preference" className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between p-3.5 bg-white rounded-2xl border border-[#E7D9BC]">
                  <div className="min-w-0">
                    <h5 id="prasad-preference-label" className="text-xs font-bold text-[#2A1F16]">
                      Do you want to receive consecrated Prasad for this Puja?
                    </h5>
                    <p className="text-[11px] text-gray-500">
                      Dispatched via express courier with holy Raksha Sutra, Vibhuti &amp; dry prasad
                    </p>
                  </div>
                  <div role="group" aria-labelledby="prasad-preference-label" className="flex shrink-0 items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
                    <button
                      type="button"
                      onClick={() => setHasPrasad(true)}
                      aria-pressed={hasPrasad}
                      className={`px-3 py-2 sm:py-1 whitespace-nowrap rounded-lg text-xs font-bold transition-all ${
                        hasPrasad ? 'bg-[#6E1B34] text-white shadow-2xs' : 'text-gray-600'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasPrasad(false)}
                      aria-pressed={!hasPrasad}
                      className={`px-3 py-2 sm:py-1 whitespace-nowrap rounded-lg text-xs font-bold transition-all ${
                        !hasPrasad ? 'bg-gray-700 text-white shadow-2xs' : 'text-gray-600'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* Delivery Address Form if Prasad is YES */}
                {hasPrasad && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-2xl border border-gray-200 space-y-3">
                    <h5 className="font-heading text-xs font-bold text-[#4E1225] uppercase tracking-wider flex items-center gap-1">
                      <Truck className="w-3.5 h-3.5 text-[#D9711F]" />
                      <span>Prasad Delivery Address</span>
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <Label className="text-[11px] text-gray-500">Recipient Name *</Label>
                        <Input
                          value={deliveryAddress.name}
                          onChange={(e) => setDeliveryAddress(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Receiver's name"
                          className="text-xs h-8 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Phone Number *</Label>
                        <Input
                          value={deliveryAddress.phone}
                          onChange={(e) => setDeliveryAddress(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="10-digit mobile"
                          className="text-xs h-8 mt-1"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] text-gray-500">Flat / House No. / Street Address *</Label>
                        <Input
                          value={deliveryAddress.address}
                          onChange={(e) => setDeliveryAddress(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="House No., Building, Street area"
                          className="text-xs h-8 mt-1"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] text-gray-500">Landmark / Colony (Optional)</Label>
                        <Input
                          value={deliveryAddress.address_line2}
                          onChange={(e) => setDeliveryAddress(prev => ({ ...prev, address_line2: e.target.value }))}
                          placeholder="Near temple, landmark"
                          className="text-xs h-8 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">City *</Label>
                        <Input
                          value={deliveryAddress.city}
                          onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="City"
                          className="text-xs h-8 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Pincode *</Label>
                        <Input
                          value={deliveryAddress.pincode}
                          onChange={(e) => setDeliveryAddress(prev => ({ ...prev, pincode: e.target.value }))}
                          placeholder="6-digit pincode"
                          className="text-xs h-8 mt-1"
                        />
                      </div>
                    </div>
                    <PrasadShipping shipping={shipping} />
                  </div>
                )}
              </div>

              {/* Step 3 Navigation */}
              <div className="pt-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="text-xs py-2 px-4 rounded-xl flex items-center gap-1 text-gray-600"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to Offerings</span>
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (validateStep3()) setStep(4);
                  }}
                  className="btn-saffron py-2.5 px-6 rounded-xl font-bold text-xs shadow-md flex items-center gap-1.5"
                >
                  <span>Continue to Puja Wish</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════ STEP 4: PUJA WISH / SANKALP ═══════════════════ */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-orange-100 text-[#D9711F] flex items-center justify-center mx-auto mb-2">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h4 className="font-heading text-lg font-bold text-[#4E1225]">
                  Puja Wish / Sankalp / Special Request
                </h4>
                <p className="text-xs text-gray-500 max-w-md mx-auto">
                  Our Acharyas will present this sacred intention before the holy deity during the hawan and abhishek rituals.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-gray-700">
                  Your Sacred Prayer / Sankalp Wish:
                </Label>
                <textarea
                  rows={4}
                  placeholder="Enter your Puja wish, Sankalp or special request (e.g. for family prosperity, good health, career growth, removing obstacles)..."
                  value={pujaWish}
                  onChange={(e) => setPujaWish(e.target.value)}
                  className="w-full rounded-2xl border border-gray-300 p-3 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#6E1B34] focus:border-transparent leading-relaxed"
                />
                <p className="text-[11px] text-gray-400">
                  Tip: You can mention specific family members, auspicious wishes, or spiritual intentions.
                </p>
              </div>

              {/* Step 4 Navigation */}
              <div className="pt-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(3)}
                  className="text-xs py-2 px-4 rounded-xl flex items-center gap-1 text-gray-600"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to Devotees</span>
                </Button>
                <Button
                  type="button"
                  onClick={() => setStep(5)}
                  className="btn-saffron py-2.5 px-6 rounded-xl font-bold text-xs shadow-md flex items-center gap-1.5"
                >
                  <span>Review &amp; Pay</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════ STEP 5: REVIEW & PAYMENT ═══════════════════ */}
          {step === 5 && pendingVerification && (
            <div className="space-y-4 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-950">
              <h4 className="font-heading font-bold text-lg">Payment verification pending</h4>
              <p>Your payment was received, but the booking confirmation needs to be retried. This will not charge you again.</p>
              <p>Amount: <strong>₹{pendingVerification.payload.amount?.toLocaleString()}</strong></p>
              <Button type="button" disabled={submitting} onClick={handleFinalSubmit} className="btn-saffron rounded-xl font-bold">
                {submitting ? 'Verifying Payment...' : 'Retry Payment Verification'}
              </Button>
            </div>
          )}
          {step === 5 && !pendingVerification && (
            <div className="space-y-6">
              {/* Order Summary Breakdown */}
              <div className="bg-[#FBF4E7] rounded-2xl p-4 border border-[#E7D9BC] space-y-3">
                <h4 className="font-heading text-xs font-bold text-[#4E1225] uppercase tracking-wider">
                  Booking Summary
                </h4>

                <div className="space-y-2 text-xs divide-y divide-[#E7D9BC]/60">
                  {/* Puja Package */}
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <p className="font-bold text-[#2A1F16]">{puja?.name}</p>
                      <p className="text-[11px] text-gray-500">Package: {selectedPackage?.name} ({namedDevotees.length} {namedDevotees.length === 1 ? 'Devotee' : 'Devotees'})</p>
                    </div>
                    <span className="font-price font-bold text-[#4E1225]">₹{packagePrice}</span>
                  </div>

                  {/* Bhet Items */}
                  {bhetTotal > 0 && (
                    <div className="pt-2 flex items-center justify-between">
                      <span className="text-gray-600">Temple Bhet &amp; Offerings</span>
                      <span className="font-price font-semibold text-gray-800">+₹{bhetTotal}</span>
                    </div>
                  )}

                  {/* Dakshina */}
                  {finalDakshina > 0 && (
                    <div className="pt-2 flex items-center justify-between">
                      <span className="text-gray-600">Pandit Dakshina 🙏🏻</span>
                      <span className="font-price font-semibold text-gray-800">+₹{finalDakshina}</span>
                    </div>
                  )}

                  {/* Store Products */}
                  {productsTotal > 0 && (
                    <div className="pt-2 flex items-center justify-between">
                      <span className="text-gray-600">Consecrated Products</span>
                      <span className="font-price font-semibold text-gray-800">+₹{productsTotal}</span>
                    </div>
                  )}

                  {/* Shipping */}
                  {hasPrasad && (
                    <div className="pt-2 flex items-center justify-between">
                      <span className="text-gray-600">Prasad Shipping{shipping.selected ? ` — ${shipping.selected.name}` : ''}</span>
                      <span className="font-price font-semibold text-gray-800">{shipping.selected ? `+₹${shippingCharge}` : 'Not selected'}</span>
                    </div>
                  )}

                  {/* Coupon Discount */}
                  {discountAmount > 0 && (
                    <div className="pt-2 flex items-center justify-between text-emerald-700 font-semibold">
                      <span>Coupon Discount ({appliedCoupon?.code})</span>
                      <span>-₹{discountAmount}</span>
                    </div>
                  )}

                  {/* Final Total */}
                  <div className="pt-2 flex items-center justify-between text-sm">
                    <span className="font-bold text-[#2A1F16]">Total Amount</span>
                    <span className="font-price font-black text-xl text-[#6E1B34]">
                      ₹{finalPayableTotal.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Devotees & Wish Confirmation Chip */}
              <div className="p-3 bg-white rounded-2xl border border-gray-200 text-xs space-y-1">
                <p className="font-bold text-gray-700">
                  Devotees: <span className="font-normal text-gray-600">{namedDevotees.map(d => `${d.name.trim()} (${d.isUnknownGotra ? 'Kashyap' : d.gotra})`).join(', ')}</span>
                </p>
                {pujaWish && (
                  <p className="font-bold text-gray-700">
                    Sankalp Wish: <span className="font-normal text-gray-600 italic">"{pujaWish}"</span>
                  </p>
                )}
              </div>

              {/* Payment Method Selector */}
              <div>
                <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-2">
                  Select Payment Method:
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    onClick={() => setPaymentMethod('razorpay')}
                    className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
                      paymentMethod === 'razorpay'
                        ? 'border-[#6E1B34] bg-[#FBF4E7]/60 shadow-xs'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 text-[#6E1B34]" />
                    <div>
                      <p className="font-bold text-xs text-[#2A1F16]">Online Payment</p>
                      <p className="text-[10px] text-gray-500">UPI, Cards, NetBanking, Wallets</p>
                    </div>
                  </div>

                  <div
                    onClick={() => setPaymentMethod('cod')}
                    className={`p-3.5 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
                      paymentMethod === 'cod'
                        ? 'border-[#6E1B34] bg-[#FBF4E7]/60 shadow-xs'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <ShieldCheck className="w-5 h-5 text-[#2E7D45]" />
                    <div>
                      <p className="font-bold text-xs text-[#2A1F16]">Book with Advance Verification</p>
                      <p className="text-[10px] text-gray-500">Pay via UPI QR / Priest coordination</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 Navigation & Pay CTA */}
              {hasPrasad && <PrasadShipping shipping={shipping} />}
              <div className="pt-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(4)}
                  className="text-xs py-2 px-4 rounded-xl flex items-center gap-1 text-gray-600"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to Wish</span>
                </Button>
                <Button
                  type="button"
                  disabled={submitting || gatewayOpen || (hasPrasad && !shipping.selected && !pendingVerification)}
                  onClick={handleFinalSubmit}
                  className="btn-saffron py-3 px-8 rounded-xl font-bold text-sm shadow-lg flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  <span>{submitting || gatewayOpen ? 'Processing Payment...' : pendingVerification ? 'Retry Payment Verification' : `Complete Booking & Pay ₹${finalPayableTotal.toLocaleString()}`}</span>
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════ STEP 6: BOOKING CONFIRMATION & RECEIPT ═══════════════════ */}
          {step === 6 && completedBooking && (
            <div className="text-center py-4 space-y-5">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-[#2E7D45] flex items-center justify-center mx-auto shadow-sm">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#B85A15] bg-[#FBF4E7] px-3 py-1 rounded-full border border-[#E7D9BC]">
                  Sacred Sankalp Confirmed
                </span>
                <h3 className="font-heading text-xl sm:text-2xl font-black text-[#4E1225] mt-2">
                  Har Har Mahadev! Your Puja has been Booked
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Booking ID: <strong className="font-mono text-[#2A1F16]">{completedBooking.id}</strong>
                </p>
              </div>

              {/* Receipt Summary Card */}
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 text-left text-xs space-y-2 max-w-md mx-auto">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-gray-500">Puja Ceremony</span>
                  <span className="font-bold text-[#2A1F16]">{puja?.name}</span>
                </div>
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-gray-500">Package</span>
                  <span className="font-semibold text-gray-800">{completedBooking.package_name}</span>
                </div>
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-gray-500">Devotees</span>
                  <span className="font-semibold text-gray-800">{completedBooking.devotee_names?.join(', ')}</span>
                </div>
                {completedBooking.puja_wish && (
                  <div className="border-b border-gray-200 pb-2">
                    <span className="text-gray-500 block mb-0.5">Sankalp Wish:</span>
                    <p className="italic text-gray-700 bg-white p-2 rounded-lg border border-gray-100">
                      "{completedBooking.puja_wish}"
                    </p>
                  </div>
                )}
                {completedBooking.has_prasad && (
                  <div className="border-b border-gray-200 pb-2">
                    <span className="text-gray-500 block mb-0.5">Prasad Delivery Address:</span>
                    <p className="text-gray-700">
                      {completedBooking.delivery_address?.name}, {completedBooking.delivery_address?.address}, {completedBooking.delivery_address?.city} - {completedBooking.delivery_address?.pincode}
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="font-bold text-gray-700">{completedBooking.payment_status === 'paid' ? 'Total Paid' : 'Total Amount'}</span>
                  <span className="font-price font-bold text-base text-[#6E1B34]">
                    ₹{completedBooking.amount?.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                <Button
                  type="button"
                  onClick={() => {
                    window.location.href = `/tracking/${completedBooking.id}`;
                  }}
                  className="btn-saffron py-2.5 px-6 rounded-xl text-xs font-bold shadow-md"
                >
                  {completedBooking.has_prasad ? 'Track Prasad & Sankalp Video' : 'Track Sankalp Video'}
                </Button>
                {user && <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    window.location.href = '/dashboard';
                  }}
                  className="py-2.5 px-6 rounded-xl text-xs font-semibold"
                >
                  Go to My Dashboard
                </Button>}
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
